import { EmptyState, Skeleton } from '@h2hspark/ui';
import { Receipt } from 'lucide-react';
import * as React from 'react';

import { PageBackLink } from '@/member/page-back-link';

import { MEMBER_PROFILE_ROUTES } from '../members/constants';

import { useMyRegisteredEvents } from './api';
import {
  NO_ORDERS_DESCRIPTION,
  NO_ORDERS_TITLE,
  TICKETS_AND_ORDERS_DESCRIPTION,
  TICKETS_AND_ORDERS_HEADING,
} from './constants';
import { RegistrationSummaryPanel } from './registration-summary-panel';

/**
 * **Tickets & orders** (`MEM-022` AC-3) — the member's own purchase history.
 *
 * ## Reached from the account page, which is the criterion
 *
 * AC-3 is *"Tickets & orders is reached from the account page"*, and the entry point is the row
 * `MEM-007` AC-6 already put there: `MEMBER_ACCOUNT_SETTINGS_ROWS`'s `tickets` entry, which
 * carried `href: null` and `notBuilt: 'MEM-022'` until this story. It now carries
 * `TICKETS_AND_ORDERS_PATH` and there is no unbuilt row left on that screen —
 * `member-identity-screens.test.ts` asserts both halves.
 *
 * The route sits under `/app/account/` rather than at `/app/orders` for the same reason: the
 * account page is where the criterion says it is reached from, and a path that says so is one
 * fewer thing for a reader to reconcile against the story.
 *
 * ## The heading and the sub-line are acceptance-criterion text
 *
 * Both are quoted verbatim in AC-3, so both are constants rather than strings in the markup, and
 * the test asserts the constants and that this screen renders them. That is the same arrangement
 * `MEM-020`'s served refusal sentences use: the sentence a test asserts is the sentence a member
 * gets.
 *
 * ## The list is registrations, and that reworded the sub-line (2026-09-03)
 *
 * It used to be one row per **order**, and this file used to argue for it: *"a free registration
 * has no order row behind it at all, so a list that promised 'every event you've registered for'
 * would be wrong."* That was a true statement about the payload and the wrong conclusion — the fix
 * was to widen the payload rather than to narrow the promise, because the member who attended a
 * free event still needs a route back to it. Sumit ruled it on 2026-09-03 and AC-3's sub-line was
 * reworded to match; `TICKETS_AND_ORDERS_DESCRIPTION` carries the argument.
 *
 * The **heading** did not move. "Tickets & orders" is still what a member finds here — every row
 * has a ticket, and the ones that took money have an order behind the Receipt control.
 *
 * ## An empty list is an answer, not an error
 *
 * A member who has registered for nothing gets `NO_ORDERS_TITLE` and a sentence saying what lands
 * here. Its old copy sent free registrations somewhere else, which was honest about a list that
 * could not hold them and is now false.
 */
export function TicketsAndOrdersPage(): React.ReactElement {
  const registrations = useMyRegisteredEvents({ enabled: true });

  return (
    <div className="space-y-4">
      <PageBackLink to={MEMBER_PROFILE_ROUTES.account}>My Account</PageBackLink>

      <header className="space-y-1">
        <h1 className="font-brand text-xl font-bold text-foreground">
          {TICKETS_AND_ORDERS_HEADING}
        </h1>
        <p className="text-sm text-muted-foreground">{TICKETS_AND_ORDERS_DESCRIPTION}</p>
      </header>

      {registrations.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-16 w-full rounded-lg" />
        </div>
      ) : registrations.data === undefined || registrations.data.registrations.length === 0 ? (
        <EmptyState
          icon={<Receipt className="h-6 w-6" aria-hidden />}
          title={NO_ORDERS_TITLE}
          description={NO_ORDERS_DESCRIPTION}
        />
      ) : (
        // The rows carry their own hairline and radius since 2026-09-12 (§2.15's row), so this is
        // a plain list rather than a card with a divided list inside it — two borders around one
        // row read as a nesting nobody intended.
        <section>
          <RegistrationSummaryPanel registrations={registrations.data.registrations} />
        </section>
      )}
    </div>
  );
}
